import { Link, useRouter } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useAuth } from '../../lib/auth/store';
import { useFocusTimer } from '../../lib/focus/store';
import { formatRemaining } from '../../lib/focus/format';

const navItems = [
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/todos', label: 'Todos' },
  { to: '/journal', label: 'Journal' },
  { to: '/mood', label: 'Mood' },
  { to: '/focus', label: 'Focus' },
  { to: '/stats', label: 'Stats' },
  { to: '/settings', label: 'Settings' },
] as const;

export function AppShell({ children }: { children: React.ReactNode }) {
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const router = useRouter();
  const [menuOpen, setMenuOpen] = useState(false);
  const remaining = useFocusTimer((s) => s.remainingMs);
  const status = useFocusTimer((s) => s.status);

  useEffect(() => {
    setMenuOpen(false);
  }, [router.state.location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="sticky top-0 z-30 bg-canvas-white/90 backdrop-blur border-b border-hairline">
        <div className="container-editorial flex items-center gap-4 sm:gap-6 h-16">
          <Link to="/dashboard" className="flex items-center gap-2 group shrink-0">
            <span className="inline-block w-2.5 h-2.5 rounded-full bg-deep-enterprise-green" aria-hidden />
            <span className="font-display text-feature-heading tracking-tight">mindrift</span>
          </Link>
          <nav aria-label="Primary" className="hidden md:flex items-center gap-1 ml-4 lg:ml-6">
            {navItems.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="px-3 py-1.5 text-caption text-ink hover:text-near-black"
                activeProps={{ className: 'text-near-black underline underline-offset-8 decoration-2' }}
                inactiveProps={{ className: 'text-muted-slate hover:text-ink' }}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="flex-1" />
          {status === 'running' && (
            <Link
              to="/focus"
              className="inline-flex items-center gap-1.5 sm:gap-2 rounded-pill border border-deep-enterprise-green text-deep-enterprise-green px-2.5 sm:px-3 py-1 text-caption shrink-0"
              aria-label={`Focus session running, ${formatRemaining(remaining)} remaining`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-deep-enterprise-green animate-pulse-soft" />
              <span className="tabular-nums">{formatRemaining(remaining)}</span>
              <span className="hidden sm:inline">focus</span>
            </Link>
          )}
          {user && (
            <div className="hidden md:flex items-center gap-3 text-caption text-muted-slate">
              <span>{user.email}</span>
              <button
                onClick={() => logout()}
                className="text-link-quiet"
                type="button"
              >
                Sign out
              </button>
            </div>
          )}
          <button
            className="md:hidden p-2 -mr-2 rounded-md border border-hairline shrink-0"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            <span className="sr-only">Menu</span>
            <span className="block w-5 h-0.5 bg-ink" />
            <span className="block w-5 h-0.5 bg-ink mt-1" />
            <span className="block w-5 h-0.5 bg-ink mt-1" />
          </button>
        </div>
        {menuOpen && (
          <>
            <button
              type="button"
              aria-label="Close menu"
              onClick={() => setMenuOpen(false)}
              className="md:hidden fixed inset-0 top-16 bg-cohere-black/30 z-20"
            />
            <div
              id="mobile-menu"
              className="md:hidden fixed top-16 inset-x-0 z-30 border-t border-hairline bg-canvas-white shadow-soft animate-fade-in-up"
            >
              <div className="container-editorial py-4 flex flex-col">
                {navItems.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    className="py-3 text-body text-ink border-b border-hairline last:border-b-0"
                    activeProps={{ className: 'py-3 text-body text-near-black font-medium border-b border-hairline last:border-b-0' }}
                  >
                    {n.label}
                  </Link>
                ))}
                {user && (
                  <div className="mt-2 pt-2 border-t border-hairline flex flex-col gap-2 text-caption text-muted-slate">
                    <span className="py-1 truncate">{user.email}</span>
                    <button
                      onClick={() => logout()}
                      className="text-link-quiet text-left py-2 self-start"
                      type="button"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <footer className="border-t border-hairline bg-canvas-white">
        <div className="container-editorial py-8 sm:py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 text-caption text-muted-slate">
          <p className="text-mono text-muted-slate">MINDRIFT · QUIET PRODUCTIVITY</p>
          <p>For your eyes only.</p>
        </div>
      </footer>
    </div>
  );
}

export function SectionTitle2({ children, className }: { children: React.ReactNode; className?: string }) {
  return <h1 className={`font-display text-section-heading tracking-tight text-balance ${className ?? ''}`}>{children}</h1>;
}
